import { ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { Home as HomeIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { NoticeBanner } from "@/components/NoticeBanner";
import { HELP_CENTER_URL } from "@/lib/supportHours";

export const GLOBAL_HEADER_HEIGHT = 35; // px, matches --global-header-height in index.css
/** Viewport height left below the app chrome (header + any active notice banner). */
export const BELOW_CHROME_HEIGHT = `calc(100vh - ${GLOBAL_HEADER_HEIGHT}px - var(--notice-banner-height, 0px))`;

const HOME_URL = "https://worldvia.pro";

/**
 * AR-210 (TC-01.5): Radix closes a hover-opened tooltip only on a later
 * pointer move, so at the top edge of the window the 'Go to worldvia.pro'
 * tooltip stayed open after focus had moved on. Controlled open state that
 * also closes when focus leaves the trigger, the pointer leaves the document
 * or the window loses focus. Same Tooltip, same content, same styling.
 */
function HeaderTooltip({ children, open, onOpenChange }: { children: ReactNode; open: boolean; onOpenChange: (o: boolean) => void }) {
  const wrapRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = () => onOpenChange(false);
    const onFocusIn = (e: FocusEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener("focusin", onFocusIn);
    document.documentElement.addEventListener("mouseleave", close);
    window.addEventListener("blur", close);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.documentElement.removeEventListener("mouseleave", close);
      window.removeEventListener("blur", close);
    };
  }, [open, onOpenChange]);
  return (
    <span ref={wrapRef} className="contents">
      <Tooltip open={open} onOpenChange={onOpenChange}>
        {children}
      </Tooltip>
    </span>
  );
}

interface GlobalHeaderProps {
  className?: string;
  tagline?: string;
  /** Rendered before the logo — used for the mobile nav trigger (AR-316). */
  leftSlot?: ReactNode;
  rightSlot?: ReactNode;
  onLogoClick?: () => void;
}

export function GlobalHeader({ className, tagline = "Travel Advisor Operating System", leftSlot, rightSlot, onLogoClick }: GlobalHeaderProps) {
  const [helpTipOpen, setHelpTipOpen] = useState(false);
  const [homeTipOpen, setHomeTipOpen] = useState(false);
  const actions = rightSlot ?? (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-center gap-2">
        <HeaderTooltip open={helpTipOpen} onOpenChange={setHelpTipOpen}>
          <TooltipTrigger asChild>
            <Button
              asChild
              variant="ghost"
              className="relative h-7 rounded-full px-3 text-xs font-semibold uppercase tracking-[0.18em] text-white/90 border border-transparent transition-colors before:absolute before:inset-x-0 before:-inset-y-2 before:content-[''] hover:border-[#fb923c] hover:text-white dark:hover:border-[#fb923c] dark:hover:text-[#fb923c] focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent sm:before:content-none"
            >
              <a
                href={HELP_CENTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open AIVIA help center"
                title="Open AIVIA help center"
              >
                Help
              </a>
            </Button>
          </TooltipTrigger>
          <TooltipContent sideOffset={6}>Open help center</TooltipContent>
        </HeaderTooltip>
        <HeaderTooltip open={homeTipOpen} onOpenChange={setHomeTipOpen}>
          <TooltipTrigger asChild>
            <Button
              asChild
              variant="ghost"
              className="relative h-7 w-7 rounded-full bg-transparent text-white transition-colors before:absolute before:inset-x-0 before:-inset-y-2 before:content-[''] hover:bg-transparent hover:text-[#b82f74] focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent sm:before:content-none"
            >
              <a
                href={HOME_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Go to WorldVia home"
                title="Go to WorldVia home"
              >
                <HomeIcon aria-hidden="true" className="h-3.5 w-3.5" />
              </a>
            </Button>
          </TooltipTrigger>
          <TooltipContent sideOffset={6}>Go to worldvia.pro</TooltipContent>
        </HeaderTooltip>
      </div>
    </TooltipProvider>
  );

  const logoElement = (
    <Link
      href="/"
      aria-label="Go to Canvas home"
      title="Go to Canvas home"
      className="relative ml-[-6px] inline-flex items-center before:absolute before:inset-x-0 before:-inset-y-2 before:content-[''] sm:before:content-none"
      onClick={onLogoClick}
    >
      <img
        src="/assets/aivia-white-crop.avif"
        alt="AIVIA"
        className="h-6 w-auto select-none drop-shadow-sm"
        draggable={false}
      />
    </Link>
  );

  const hasRightSlot = Boolean(actions);

  return (
    <>
    <header
      role="banner"
      className={cn(
        "sticky top-0 z-50 flex h-[var(--global-header-height,35px)] w-full items-center gap-3 px-3 sm:px-5",
        "backdrop-blur-sm shadow-[0_1px_4px_rgba(0,0,0,0.08)] border-b border-white/20 dark:border-white/10",
        hasRightSlot ? "justify-between" : "justify-start",
        className,
      )}
      style={{
        backgroundImage: "var(--global-header-gradient)",
        color: "var(--global-header-text, rgba(255,255,255,0.94))",
      }}
      data-testid="global-header"
      data-app-chrome
    >
      <div className="flex min-w-0 items-center gap-2">
        {leftSlot}
        {logoElement}
        <span className="relative top-[2px] hidden truncate text-xs font-medium uppercase tracking-[0.24em] text-white/90 sm:inline">
          {tagline}
        </span>
      </div>
      {hasRightSlot ? (
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-white/80">
          {actions}
        </div>
      ) : null}
    </header>
    <NoticeBanner />
    </>
  );
}

export default GlobalHeader;
